import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { ScrollArea } from "../components/ui/scroll-area";

export default function Coach() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/coach/history`);
      setMessages(res.data.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp
      })));
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/coach/chat`, { message: userMessage });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      toast.error("Coach is unavailable. Try again.");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear all conversation history?")) return;
    try {
      await axios.delete(`${API}/coach/history`);
      setMessages([]);
      toast.success("History cleared");
    } catch (err) {
      toast.error("Failed to clear history");
    }
  };

  const quickPrompts = [
    "Analyze my last trade setup",
    "What makes a valid order block?",
    "How do I identify liquidity sweeps?",
    "Explain the FU candle strategy",
    "I keep revenge trading. Help.",
    "What's a good R:R ratio?",
  ];

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={i} className="font-bold text-white my-2">{line.slice(2, -2)}</p>;
        }
        if (line.startsWith('- ')) {
          return <p key={i} className="ml-4 my-1">• {line.slice(2)}</p>;
        }
        if (line.match(/^\d+\./)) {
          return <p key={i} className="ml-4 my-1">{line}</p>;
        }
        return <p key={i} className="my-1">{line}</p>;
      });
  };

  return (
    <div className="h-screen flex flex-col" data-testid="coach-page">
      {/* Header */}
      <div className="p-6 border-b border-[#262626] flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 border border-blue-500/50 bg-blue-500/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-mono font-bold text-white">COACH BASHIRAH</h1>
            <p className="text-xs text-slate-500 font-mono">Wisdom of Dr. Chris Cole. Precision of an institution.</p>
          </div>
        </div>
        <button
          onClick={clearHistory}
          className="btn-secondary text-xs"
          data-testid="clear-history-btn"
        >
          CLEAR HISTORY
        </button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Welcome Message */}
            {messages.length === 0 && !loadingHistory && (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 border border-blue-500/30 bg-blue-500/5 flex items-center justify-center">
                  <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="font-mono text-xl text-white mb-2">Ready to elevate your game?</h2>
                <p className="text-slate-500 max-w-md mx-auto mb-8">
                  I'm Coach Bashirah, here to help you think like an institution using Dr. Chris Cole's methodology. Ask about setups, psychology, or review your trades. No questions are stupid - only unasked ones cost money.
                </p>
                
                {/* Quick Prompts */}
                <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                  {quickPrompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(prompt)}
                      className="text-xs font-mono px-3 py-2 border border-[#262626] text-slate-400 hover:text-white hover:border-blue-500/50 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
              >
                <div className={`max-w-[80%] ${msg.role === "user" ? "order-2" : "order-1"}`}>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 border border-blue-500/50 bg-blue-500/10 flex items-center justify-center">
                        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-7-5z" />
                        </svg>
                      </div>
                      <span className="text-xs font-mono text-blue-500">COACH BASHIRAH</span>
                    </div>
                  )}
                  <div className={`p-4 ${
                    msg.role === "user"
                      ? "bg-blue-500/10 border border-blue-500/30 text-white"
                      : "bg-[#0A0A0A] border border-[#262626] text-slate-300 terminal-text"
                  }`}>
                    {msg.role === "user" ? (
                      <p className="text-sm">{msg.content}</p>
                    ) : (
                      <div className="text-sm space-y-1">{formatMessage(msg.content)}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-[#0A0A0A] border border-[#262626] p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-[#262626] bg-[#050505]">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="flex gap-4">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the coach anything..."
                data-testid="coach-input"
                className="flex-1 input-institutional px-4 py-3"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="send-message-btn"
                className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "..." : "SEND"}
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2 text-center font-mono">
              Press Enter to send • The market rewards patience
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
